import { useMemo } from "react"
import { get } from "lodash"

import { useVenue } from "providers/venue"

import { DEFAULT_VENUE_FILTER } from "constant"
import { UseFeatureQuickSearch } from "./types"

import {
  getCategoriesFromFeatures,
  getCategoryOptions,
  getFeatureQuickSearchConfigByVenue,
} from "./utils"

export const useFeatureQuickSearch: UseFeatureQuickSearch = (
  category,
  viewingVenueId
) => {
  const {
    occupants,
    amenities,
    openings,
    venues,
    featureQuickSearchConfig,
    venueFeatureQuickSearchConfig,
  } = useVenue()
  const currentVenue = useMemo(
    () => venues.find((venue) => venue?.id === viewingVenueId),
    [venues, viewingVenueId]
  )

  const features = useMemo(
    () =>
      [...amenities, ...occupants, ...openings].filter(
        (feature) =>
          !viewingVenueId ||
          viewingVenueId === DEFAULT_VENUE_FILTER ||
          feature?.properties?.venue?.id === viewingVenueId
      ),
    [occupants, amenities, openings, viewingVenueId]
  )

  /**
   * Use `venueFeatureQuickSearch` from app config for amenity filters, or fallback to `featureQuickSearch`.
   */
  const categories = useMemo(() => {
    const venueReference = get(currentVenue, "properties.reference")

    // If all feature quick search exists in venueFeatureQuickSearch, use it as fallback when relatedVenueFeatureQuickSearchConfig has no match.
    const relatedVenuefeatureQuickSearchConfig =
      getFeatureQuickSearchConfigByVenue(
        venueFeatureQuickSearchConfig,
        venueReference
      ) ||
      getFeatureQuickSearchConfigByVenue(venueFeatureQuickSearchConfig, "all")

    return relatedVenuefeatureQuickSearchConfig
      ? getCategoryOptions(relatedVenuefeatureQuickSearchConfig)
      : getCategoriesFromFeatures(features, featureQuickSearchConfig)
  }, [
    features,
    featureQuickSearchConfig,
    venueFeatureQuickSearchConfig,
    currentVenue,
  ])

  const quicksearchFeatures = useMemo(
    () =>
      features.filter((amenity) =>
        `${amenity.properties.category}`.startsWith(category)
      ),
    [features, category]
  )

  return {
    features: quicksearchFeatures,
    categories,
  }
}
